import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import EmptyState from "@/components/base/EmptyState";
import { news } from "@/mocks/news";

const CATEGORIES = ["全部", "影视新闻", "行业动态", "影片预告"];
const PAGE_SIZE = 4;

export default function News() {
  const [category, setCategory] = useState("全部");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    document.title = "影视资讯 - 白虎影视平台";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "白虎影视资讯频道，提供影视新闻、行业动态与影片预告等最新内容，帮助你及时了解影视圈动态。"
      );
    }
  }, []);

  const filtered = useMemo(
    () => (category === "全部" ? news : news.filter((item) => item.category === category)),
    [category]
  );

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const changeCategory = (value: string) => {
    setCategory(value);
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-accent-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">影视资讯</span>
      </nav>

      <header className="mt-5">
        <h1 className="text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
          影视资讯
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          汇集影视新闻、行业动态与影片预告，第一时间掌握影视圈的最新消息。本站资讯为独立内容展示，不包含播放与下载功能。
        </p>
      </header>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {CATEGORIES.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => changeCategory(item)}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              category === item
                ? "bg-accent-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {item}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {filtered.length === 0 ? (
          <EmptyState
            icon="ri-newspaper-line"
            title="当前分类暂无资讯"
            description="该分类下暂时还没有内容，切换其他分类看看吧。"
          />
        ) : (
          <>
            <ul className="space-y-5">
              {visible.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/news/detail/${item.id}`}
                    className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-accent-300 sm:flex-row"
                  >
                    <div className="relative h-44 w-full shrink-0 overflow-hidden bg-background-200 sm:h-auto sm:w-64">
                      <img
                        src={item.cover}
                        alt={`${item.title} 配图`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                        <span className="rounded-md bg-secondary-100 px-2 py-0.5 font-medium text-secondary-800">
                          {item.category}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {item.publishDate}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i>
                          {item.source}
                        </span>
                      </div>
                      <h2 className="mt-3 text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-accent-600 md:text-lg">
                        {item.title}
                      </h2>
                      <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
                        {item.summary}
                      </p>
                      <span className="mt-4 flex items-center gap-1 text-sm font-medium text-accent-600">
                        阅读全文
                        <i className="ri-arrow-right-s-line text-lg"></i>
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            {hasMore ? (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-accent-400 hover:text-accent-600"
                >
                  <i className="ri-add-line"></i>
                  加载更多
                </button>
              </div>
            ) : (
              <p className="mt-8 text-center text-sm text-foreground-400">已经到底啦</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}